import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getScopeLabel,
    getReviewStatusLabel,
    getTaskStatusLabel,
    getTaskTypeLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
    getPriorityLabel,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getPmoTooltip,
    getTaskStatusTone,
    getReviewStatusTone,
    getRouteStatusTone,
    getEvidenceStatusTone,
    getHealthTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";



const columnMeta = {
    pendiente: {
        title: "Pendiente",
        subtitle: "Trabajo por iniciar",
        color: "#64748B",
        bg: "#F8FAFC",
    },
    en_proceso: {
        title: "En proceso",
        subtitle: "Trabajo activo",
        color: "#2563EB",
        bg: "#EFF6FF",
    },
    en_revision: {
        title: "En revisión",
        subtitle: "Esperando aprobación",
        color: "#7C3AED",
        bg: "#F5F3FF",
    },
    con_cambios: {
        title: "Con cambios",
        subtitle: "Corrección requerida",
        color: "#F59E0B",
        bg: "#FFFBEB",
    },
    bloqueada: {
        title: "Bloqueada",
        subtitle: "Requiere intervención",
        color: "#DC2626",
        bg: "#FFF1F2",
    },
    terminada: {
        title: "Terminada",
        subtitle: "Trabajo cerrado",
        color: "#16A34A",
        bg: "#ECFDF5",
    },
};

function safeNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatLabel(value) {
    return String(value || "sin estado")
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function initials(name = "Usuario") {
    return String(name)
        .trim()
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((x) => x[0]?.toUpperCase())
        .join("") || "U";
}

function priorityStyle(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") {
        return { bg: "#FFF1F2", color: "#BE123C", border: "#FECDD3" };
    }

    if (p === "alta") {
        return { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" };
    }

    if (p === "media") {
        return { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" };
    }

    return { bg: "#F8FAFC", color: "#475569", border: "#E2E8F0" };
}

function priorityTooltip(priority) {
    const p = String(priority || "").toLowerCase();

    if (p === "critica") {
        return "Prioridad crítica: tarea de alto impacto que puede afectar fechas, entregables o decisiones ejecutivas si no se atiende.";
    }

    if (p === "alta") {
        return "Prioridad alta: tarea importante para el avance del proyecto; requiere seguimiento cercano.";
    }

    if (p === "media") {
        return "Prioridad media: tarea relevante para la operación, sin nivel crítico o alto asignado.";
    }

    return "Prioridad baja o no definida: tarea de seguimiento operativo sin urgencia alta registrada.";
}

function taskStatusTooltip(task) {
    const status = String(task?.status || "").toLowerCase();

    if (status === "terminada") return "Terminada: la tarea quedó cerrada operativamente dentro del proyecto.";
    if (status === "en_proceso") return "En proceso: la tarea tiene trabajo activo o avance registrado, pero aún no está cerrada.";
    if (status === "pendiente") return "Pendiente: la tarea todavía no inicia formalmente o no tiene avance registrado.";
    if (status === "bloqueada") return "Bloqueada: la tarea requiere intervención antes de continuar.";
    if (status === "cancelada") return "Cancelada: la tarea ya no forma parte del trabajo activo del proyecto.";

    return "Estado operativo de la tarea dentro del flujo PMO.";
}

function reviewTooltip(task) {
    const review = String(task?.review_status || "").toLowerCase();
    const status = String(task?.status || "").toLowerCase();

    if (review === "aprobado" && status !== "terminada") {
        return "Aprobado parcial: el último avance fue validado, pero la tarea sigue abierta porque aún no se ha cerrado al 100%.";
    }

    if (review === "aprobado") return "Aprobado: la revisión fue validada y el trabajo quedó aceptado.";
    if (review === "en_revision") return "En revisión: el avance fue enviado y está pendiente de decisión del revisor.";
    if (review === "rechazado") return "Rechazado: el avance necesita corrección antes de continuar.";
    if (review === "pendiente_revision") return "Pendiente de revisión: la tarea requiere validación antes de cerrar el ciclo.";

    return "Sin revisión activa: la tarea no tiene un flujo de revisión abierto en este momento.";
}

function routeTooltip(task) {
    if (safeNumber(task?.dependency_is_blocked || task?.is_blocked) === 1) {
        return "Ruta bloqueada: la tarea no puede avanzar porque su predecesora aún no cumple la condición de dependencia.";
    }

    if (task?.depends_on_task_id) {
        const type = task?.dependency_type || "FS";
        return `Ruta liberada: la dependencia ${type} ya cumple la condición necesaria para que esta tarea continúe.`;
    }

    return "Sin dependencia activa: la tarea no está detenida por una ruta previa.";
}

function evidenceTooltip(task) {
    const needsEvidence = safeNumber(task?.evidence_required) === 1;
    const needsGeo = safeNumber(task?.geo_evidence_required) === 1;
    const evidenceCount = safeNumber(task?.evidence_count);
    const geoCount = safeNumber(task?.geo_evidence_count);

    if (needsGeo && geoCount === 0) {
        return "Falta geoevidencia: la tarea requiere evidencia con ubicación capturada para completar trazabilidad.";
    }

    if (needsEvidence && evidenceCount === 0) {
        return "Falta evidencia: la tarea requiere soporte documental antes de enviarse, revisarse o cerrarse.";
    }

    if (needsGeo || needsEvidence) {
        return "Evidencia completa: la tarea cumple con la evidencia requerida para su trazabilidad.";
    }

    return "Sin evidencia requerida: esta tarea no exige soporte documental obligatorio.";
}

function taskTypeTooltip(task) {
    if (task?.parent_task_id === null || task?.parent_task_id === undefined) {
        return "Paquete WBS: esta tarea funciona como fase o paquete de control y puede agrupar tareas hijas.";
    }

    return "Tarea operativa: actividad hija dentro de un paquete WBS del proyecto.";
}

function progressTooltip(task) {
    const progress = safeNumber(task?.progress_percent);

    if (progress >= 100) return "Avance 100%: la tarea está completada; debe estar cerrada o validada según sus reglas de revisión.";
    if (progress > 0) return `Avance ${progress.toFixed(0)}%: progreso operativo registrado; la tarea aún sigue abierta.`;

    return "Avance 0%: la tarea todavía no tiene progreso registrado.";
}

function dateTooltip(task) {
    if (safeNumber(task?.is_overdue) === 1) {
        return `${safeNumber(task?.days_overdue)} día(s) vencida: la tarea superó su fecha objetivo y aún no está cerrada. Requiere cierre, reprogramación o justificación.`;
    }

    if (task?.due_date) {
        return "Fecha objetivo: compromiso planeado para completar o revisar esta tarea.";
    }

    return "Sin fecha objetivo: la tarea no tiene una fecha de compromiso registrada.";
}

function reviewerTooltip(task) {
    if (task?.reviewer_user_id || task?.reviewer_name) {
        return "Revisor asignado: persona responsable de validar avances, evidencias o cierre de esta tarea.";
    }

    return "Sin revisor asignado: la tarea no tiene responsable formal de validación.";
}

function quickFilterTooltip(key) {
    const map = {
        all: "Muestra todas las tareas del proyecto o portafolio seleccionado.",
        without_reviewer: "Filtra tareas activas sin revisor asignado para corregir gobierno y validación.",
        without_due_date: "Filtra tareas activas sin fecha objetivo registrada.",
        without_evidence: "Filtra tareas activas que requieren evidencia y todavía no tienen soporte cargado.",
        without_geo: "Filtra tareas activas que requieren geoevidencia y todavía no tienen ubicación capturada.",
        overdue: "Filtra tareas activas fuera de fecha que requieren cierre, reprogramación o justificación.",
        inconsistent: "Filtra tareas con avance y estado que no coinciden con las reglas operativas del workflow.",
    };

    return map[key] || "Filtro rápido del tablero Kanban PMO.";
}

function columnTooltip(columnKey) {
    const map = {
        pendiente: "Pendiente: tareas que aún no inician formalmente o no tienen avance registrado.",
        en_proceso: "En proceso: tareas con trabajo activo o avance registrado que aún no cierran.",
        en_revision: "En revisión: tareas enviadas a validación y pendientes de decisión del revisor.",
        con_cambios: "Con cambios: tareas que requieren corrección antes de continuar o cerrar.",
        bloqueada: "Bloqueada: tareas detenidas por dependencia, evidencia, fecha, revisor u otra condición operativa.",
        terminada: "Terminada: tareas cerradas operativamente dentro del proyecto.",
    };

    return map[columnKey] || "Columna del flujo operativo Kanban PMO.";
}

function Pill({ children, styleType = "neutral", title }) {
    const styles = PMO_BADGE_TONES;
    const s = styles[styleType] || styles.neutral;
    const tooltip = title || getPmoTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px 8px",
                borderRadius: 999,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                fontSize: 10,
                fontWeight: 900,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

function ProgressBar({ value, title }) {
    const v = Math.max(0, Math.min(100, safeNumber(value)));

    let color = "#2563EB";
    if (v >= 100) color = "#16A34A";
    if (v < 50) color = "#F59E0B";

    return (
        <div title={title}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#64748B",
                }}
            >
                <span>Avance</span>
                <span>{v.toFixed(0)}%</span>
            </div>
            <div
                style={{
                    height: 8,
                    borderRadius: 999,
                    background: "#E2E8F0",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${v}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: color,
                    }}
                />
            </div>
        </div>
    );
}

function UserMini({ name, title }) {
    return (
        <div title={title} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <div
                style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2563EB, #7C3AED)",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                {initials(name)}
            </div>
            <div
                style={{
                    minWidth: 0,
                    color: "#334155",
                    fontSize: 11,
                    fontWeight: 850,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {name || "Sin responsable"}
            </div>
        </div>
    );
}

function TaskCard({ task, onOpen }) {
    const p = priorityStyle(task.priority);
    const overdue = safeNumber(task.is_overdue) === 1;
    const needsEvidence =
        safeNumber(task.evidence_required) === 1 && safeNumber(task.evidence_count) === 0;
    const needsGeo =
        safeNumber(task.geo_evidence_required) === 1 && safeNumber(task.geo_evidence_count) === 0;
    const inconsistent =
        String(task.status) === "pendiente" && safeNumber(task.progress_percent) >= 80;
    const withoutReviewer =
        !task.reviewer_user_id &&
        String(task.status) !== "terminada" &&
        String(task.status) !== "cancelada";

    return (
        <button
            type="button"
            title="Abrir tarea para revisar detalle, registrar avance o atender la condición operativa."
            onClick={() => onOpen?.(task)}
            style={{
                width: "100%",
                textAlign: "left",
                border: overdue ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
                background: "#FFFFFF",
                borderRadius: 18,
                padding: 14,
                cursor: "pointer",
                boxShadow: overdue
                    ? "0 14px 30px rgba(220,38,38,.10)"
                    : "0 12px 24px rgba(15,23,42,.05)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            color: "#0F172A",
                            fontSize: 13,
                            fontWeight: 950,
                            lineHeight: 1.25,
                            marginBottom: 5,
                        }}
                    >
                        {task.wbs_code ? `${task.wbs_code} · ` : ""}
                        {task.task_name}
                    </div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 750,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {task.project_name}
                    </div>
                </div>

                <span
                    title={priorityTooltip(task.priority)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: 24,
                        padding: "0 8px",
                        borderRadius: 999,
                        background: p.bg,
                        color: p.color,
                        border: `1px solid ${p.border}`,
                        fontSize: 10,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                    }}
                >
                    {getPriorityLabel(task.priority)}
                </span>
            </div>

            <div style={{ marginTop: 12 }}>
                <ProgressBar value={task.progress_percent} title={progressTooltip(task)} />
            </div>

            <div style={{ marginTop: 12 }}>
                <UserMini name={task.owner_name} title="Responsable operativo asignado a esta tarea." />
            </div>

            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 12 }}>
                {overdue ? (
                    <Pill styleType="danger" title={dateTooltip(task)}>
                        {safeNumber(task.days_overdue)} días vencida
                    </Pill>
                ) : task.due_date ? (
                    <Pill styleType="neutral" title={dateTooltip(task)}>vence {task.due_date}</Pill>
                ) : (
                    <Pill styleType="warning" title={dateTooltip(task)}>sin fecha objetivo</Pill>
                )}

                <Pill styleType="info" title={taskTypeTooltip(task)}>{getTaskTypeLabel(task)}</Pill>
                <Pill styleType={getTaskStatusTone(task.status)} title={taskStatusTooltip(task)}>{getTaskStatusLabel(task.status)}</Pill>
                <Pill styleType={getReviewStatusTone(task)} title={reviewTooltip(task)}>
                    {getReviewStatusLabel(task)}
                </Pill>
                <Pill styleType={getRouteStatusTone(task)} title={routeTooltip(task)}>
                    {getRouteStatusLabel(task)}
                </Pill>
                <Pill styleType={getEvidenceStatusTone(task)} title={evidenceTooltip(task)}>
                    {getEvidenceStatusLabel(task)}
                </Pill>
                {withoutReviewer ? <Pill styleType="warning" title={reviewerTooltip(task)}>sin revisor</Pill> : null}
                {inconsistent ? <Pill styleType="danger" title="Avance inconsistente: el porcentaje registrado no coincide con el estado operativo esperado de la tarea.">avance inconsistente</Pill> : null}
            </div>
        </button>
    );
}

function KanbanColumn({ columnKey, tasks = [], onOpenTask }) {
    const meta = columnMeta[columnKey] || columnMeta.pendiente;

    return (
        <section
            title={columnTooltip(columnKey)}
            style={{
                minWidth: 320,
                maxWidth: 360,
                flex: "1 0 320px",
                background: meta.bg,
                border: "1px solid #E2E8F0",
                borderRadius: 24,
                padding: 14,
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 210px)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    marginBottom: 12,
                    padding: "4px 4px 10px",
                    borderBottom: "1px solid rgba(148,163,184,.25)",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            color: "#0F172A",
                            fontSize: 14,
                            fontWeight: 950,
                        }}
                    >
                        <span
                            style={{
                                width: 9,
                                height: 9,
                                borderRadius: 999,
                                background: meta.color,
                                boxShadow: `0 0 0 5px ${meta.color}22`,
                            }}
                        />
                        {meta.title}
                    </div>
                    <div
                        style={{
                            color: "#64748B",
                            fontSize: 11,
                            fontWeight: 750,
                            marginTop: 3,
                        }}
                    >
                        {meta.subtitle}
                    </div>
                </div>

                <div
                    title={`Total de tareas en la columna ${meta.title}.`}
                    style={{
                        minWidth: 34,
                        height: 30,
                        borderRadius: 999,
                        background: "#FFFFFF",
                        color: meta.color,
                        border: `1px solid ${meta.color}33`,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 950,
                    }}
                >
                    {tasks.length}
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gap: 12,
                    overflowY: "auto",
                    paddingRight: 3,
                }}
            >
                {tasks.length ? (
                    tasks.map((task) => (
                        <TaskCard
                            key={task.task_id}
                            task={task}
                            onOpen={onOpenTask}
                        />
                    ))
                ) : (
                    <div
                        style={{
                            border: "1px dashed #CBD5E1",
                            borderRadius: 18,
                            padding: 18,
                            color: "#64748B",
                            fontSize: 12,
                            fontWeight: 800,
                            textAlign: "center",
                            background: "rgba(255,255,255,.62)",
                        }}
                    >
                        Sin tareas en esta columna.
                    </div>
                )}
            </div>
        </section>
    );
}


function ScopeNotice({ scope, fallbackLabel, fallbackDescription }) {
    const labelText = scope?.label || fallbackLabel;
    const descriptionText = scope?.description || fallbackDescription;

    return (
        <div
            style={{
                margin: "0 0 18px",
                borderRadius: 20,
                background: "#FFFFFF",
                border: "1px solid #DBEAFE",
                padding: "14px 16px",
                boxShadow: "0 12px 28px rgba(37,99,235,.06)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
            }}
        >
            <div
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 14,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 950,
                    flex: "0 0 auto",
                }}
            >
                ⌖
            </div>
            <div>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>
                    Alcance de la vista: {labelText}
                </div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                    {descriptionText}
                </div>
            </div>
        </div>
    );
}

export default function KanbanPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedReviewerId, setSelectedReviewerId] = useState(() => {
        return searchParams.get("reviewer_id") || "";
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [search, setSearch] = useState(() => {
        return searchParams.get("q") || "";
    });
    const [selectedProjectId, setSelectedProjectId] = useState(() => {
        return searchParams.get("project_id") || "all";
    });
    const [quickFilter, setQuickFilter] = useState(() => {
        return searchParams.get("governance") || searchParams.get("quick") || "all";
    });

    const changeQuickFilter = (value) => {
        setQuickFilter(value);

        const next = new URLSearchParams(searchParams);

        if (!value || value === "all") {
            next.delete("governance");
        } else {
            next.set("governance", value);
        }

        setSearchParams(next);
    };

    const loadKanban = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("cani_auth") || "";

            const qp = new URLSearchParams();

            if (selectedProjectId !== "all") {
                qp.set("project_id", selectedProjectId);
            }

            const response = await fetch(`${API_BASE}/pmo_kanban.php${qp.toString() ? `?${qp.toString()}` : ""}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : "",
                    "X-CANI-AUTH": token || "",
                },
            });

            const txt = await response.text();

            if (!txt || !txt.trim()) {
                throw new Error("Respuesta vacía del Kanban PMO");
            }

            const json = JSON.parse(txt);

            if (!response.ok || !json.success) {
                throw new Error(json.message || "No se pudo cargar Kanban PMO");
            }

            setData(json.data || null);
        } catch (e) {
            setError(e.message || "Error al cargar Kanban PMO");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadKanban();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProjectId]);

    useEffect(() => {
        const governance = searchParams.get("governance") || searchParams.get("quick") || "all";
        setQuickFilter(governance);
    }, [searchParams]);

    useEffect(() => {
        const reviewerFromUrl = searchParams.get("reviewer_id") || "";
        setSelectedReviewerId(reviewerFromUrl);
    }, [searchParams]);

    const filteredColumns = useMemo(() => {
        const columns = data?.columns || {};
        const q = search.trim().toLowerCase();

        const matchesQuickFilter = (task) => {
            const isActive =
                String(task.status) !== "terminada" &&
                String(task.status) !== "cancelada";

            if (quickFilter === "without_reviewer") {
                return isActive && !task.reviewer_user_id;
            }

            if (quickFilter === "without_evidence") {
                return (
                    isActive &&
                    safeNumber(task.evidence_required) === 1 &&
                    safeNumber(task.evidence_count) === 0
                );
            }

            if (quickFilter === "without_geo") {
                return (
                    isActive &&
                    safeNumber(task.geo_evidence_required) === 1 &&
                    safeNumber(task.geo_evidence_count) === 0
                );
            }
            if (quickFilter === "without_due_date") {
                return isActive && !task.due_date;
            }

            if (quickFilter === "overdue") {
                return isActive && safeNumber(task.is_overdue) === 1;
            }

            if (quickFilter === "inconsistent") {
                return (
                    String(task.status) === "pendiente" &&
                    safeNumber(task.progress_percent) >= 80
                );
            }

            return true;
        };

        const next = {};

        Object.entries(columns).forEach(([key, tasks]) => {
            next[key] = (tasks || []).filter((task) => {
                const matchesSearch = !q
                    ? true
                    : [
                        task.task_name,
                        task.project_name,
                        task.owner_name,
                        task.reviewer_name,
                        task.priority,
                        task.status,
                        task.review_status,
                        task.wbs_code,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase()
                        .includes(q);

                if (
                    selectedReviewerId &&
                    String(task.reviewer_user_id || "") !== String(selectedReviewerId)
                ) {
                    return false;
                }

                return matchesSearch && matchesQuickFilter(task);
            });
        });

        return next;
    }, [data, search, quickFilter, selectedReviewerId]);


    const summary = data?.summary || {};
    const scope = data?.scope || {};

    const projectOptions = useMemo(() => {
        return (data?.project_options || data?.projects || []).map((p) => ({
            id: String(p.project_id),
            name: p.project_name,
        }));
    }, [data]);

    const selectedReviewerName = useMemo(() => {
        if (!selectedReviewerId) return "";

        const columns = data?.columns || {};
        const allTasks = Object.values(columns).flatMap((tasks) => tasks || []);
        const found = allTasks.find(
            (task) => String(task.reviewer_user_id || "") === String(selectedReviewerId)
        );

        return found?.reviewer_name || `#${selectedReviewerId}`;
    }, [data, selectedReviewerId]);

    const openTask = (task) => {
        if (!task?.task_id) return;

        const returnParams = new URLSearchParams();

        if (selectedProjectId && selectedProjectId !== "all") {
            returnParams.set("project_id", selectedProjectId);
        }

        if (quickFilter && quickFilter !== "all") {
            returnParams.set("governance", quickFilter);
        }

        if (selectedReviewerId) {
            returnParams.set("reviewer_id", selectedReviewerId);
        }

        if (search && search.trim()) {
            returnParams.set("q", search.trim());
        }

        const returnTo = `/kanban${returnParams.toString() ? `?${returnParams.toString()}` : ""}`;

        const qp = new URLSearchParams();
        qp.set("mode", "progress");
        qp.set("return_to", returnTo);
        qp.set("return_label", "Kanban PMO");

        navigate(`/my-work/task/${task.task_id}?${qp.toString()}`);
    };

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#64748B",
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                Cargando Kanban PMO...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    borderRadius: 20,
                    background: "#FFF1F2",
                    border: "1px solid #FECDD3",
                    color: "#BE123C",
                    padding: 20,
                    fontSize: 14,
                    fontWeight: 850,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <section
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 24,
                    background:
                        "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 58%, #EEF2FF 100%)",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 20px 50px rgba(15,23,42,.07)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 20,
                }}
            >
                <div>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "7px 11px",
                            borderRadius: 999,
                            background: "#EFF6FF",
                            border: "1px solid #BFDBFE",
                            color: "#1D4ED8",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: ".06em",
                            textTransform: "uppercase",
                            marginBottom: 14,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: "#2563EB",
                            }}
                        />
                        Flujo operativo
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 32,
                            lineHeight: 1.05,
                            color: "#0F172A",
                            fontWeight: 950,
                            letterSpacing: "-.05em",
                        }}
                    >
                        Kanban PMO
                    </h1>

                    <p
                        style={{
                            margin: "10px 0 0",
                            color: "#64748B",
                            fontSize: 14,
                            lineHeight: 1.5,
                            maxWidth: 780,
                            fontWeight: 700,
                        }}
                    >
                        Visualiza el flujo real de trabajo del proyecto completo por estado, revisión, prioridad, vencimiento, evidencia, dependencia y responsable.
                    </p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(90px, 1fr))",
                        gap: 10,
                        minWidth: 330,
                    }}
                >
                    <KpiMini label="Total" value={summary.total_tasks || 0} color="#0F172A" />
                    <KpiMini label="En proceso" value={summary.en_proceso || 0} color="#2563EB" />
                    <KpiMini label="En revisión" value={summary.en_revision || 0} color="#7C3AED" />
                </div>
            </section>

            <ScopeNotice
                scope={scope}
                fallbackLabel="Proyecto completo / Portafolio PMO"
                fallbackDescription="Visualiza todas las tareas del proyecto seleccionado por estado operativo, revision, evidencia, dependencia y responsable."
            />

            <section
                style={{
                    marginBottom: 18,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <section
                    style={{
                        marginBottom: 0,
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    {[
                        { key: "all", label: "Todas" },
                        { key: "without_reviewer", label: "Sin revisor" },
                        { key: "without_due_date", label: "Sin fecha" },
                        { key: "without_evidence", label: "Sin evidencia" },
                        { key: "without_geo", label: "Sin geo" },
                        { key: "overdue", label: "Vencidas" },
                        { key: "inconsistent", label: "Avance inconsistente" },
                    ].map((item) => {
                        const active = quickFilter === item.key;

                        return (
                            <button
                                key={item.key}
                                type="button"
                                title={quickFilterTooltip(item.key)}
                                onClick={() => changeQuickFilter(item.key)}
                                style={{
                                    height: 42,
                                    padding: "0 14px",
                                    borderRadius: 999,
                                    border: active ? "1px solid #0F172A" : "1px solid #E2E8F0",
                                    background: active ? "#0F172A" : "#FFFFFF",
                                    color: active ? "#FFFFFF" : "#475569",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    boxShadow: active
                                        ? "0 12px 24px rgba(15,23,42,.18)"
                                        : "0 8px 18px rgba(15,23,42,.04)",
                                }}
                            >
                                {item.label}
                            </button>
                        );
                    })}

                    {selectedReviewerId ? (
                        <span
                            title="Filtro activo por revisor: el tablero muestra únicamente tareas asignadas a este revisor."
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                height: 42,
                                padding: "0 14px",
                                borderRadius: 999,
                                background: "#EFF6FF",
                                color: "#1D4ED8",
                                border: "1px solid #BFDBFE",
                                fontSize: 12,
                                fontWeight: 900,
                            }}
                        >
                            Revisor: {selectedReviewerName || `#${selectedReviewerId}`}
                        </span>
                    ) : null}
                </section>
                <select
                    title="Selecciona el alcance del tablero: todos los proyectos para Portafolio PMO o un proyecto específico para revisión completa."
                    value={selectedProjectId}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedProjectId(value);

                        const next = new URLSearchParams(searchParams);

                        if (!value || value === "all") {
                            next.delete("project_id");
                        } else {
                            next.set("project_id", value);
                        }

                        setSearchParams(next);
                    }}
                    style={{
                        height: 42,
                        minWidth: 260,
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 800,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                >
                    <option value="all">Portafolio PMO</option>
                    {projectOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
                <input
                    title="Busca por tarea, proyecto, responsable, revisor, prioridad, estado o código WBS."
                    value={search}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSearch(value);

                        const next = new URLSearchParams(searchParams);

                        if (!value.trim()) {
                            next.delete("q");
                        } else {
                            next.set("q", value.trim());
                        }

                        setSearchParams(next);
                    }}
                    placeholder="Buscar tarea, proyecto, responsable, prioridad..."
                    style={{
                        height: 42,
                        minWidth: 340,
                        flex: "1 1 420px",
                        borderRadius: 999,
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        padding: "0 16px",
                        color: "#0F172A",
                        fontSize: 13,
                        fontWeight: 700,
                        outline: "none",
                        boxShadow: "0 12px 25px rgba(15,23,42,.04)",
                    }}
                />

                <button
                    type="button"
                    title="Recargar el Kanban PMO con los filtros actuales."
                    onClick={loadKanban}
                    style={{
                        height: 42,
                        padding: "0 16px",
                        borderRadius: 999,
                        border: "1px solid #0F172A",
                        background: "#0F172A",
                        color: "#FFFFFF",
                        fontSize: 12,
                        fontWeight: 950,
                        cursor: "pointer",
                        boxShadow: "0 14px 28px rgba(15,23,42,.18)",
                    }}
                >
                    Actualizar
                </button>
            </section>

            <section
                style={{
                    display: "flex",
                    gap: 16,
                    overflowX: "auto",
                    paddingBottom: 12,
                }}
            >
                {Object.keys(columnMeta).map((key) => (
                    <KanbanColumn
                        key={key}
                        columnKey={key}
                        tasks={filteredColumns?.[key] || []}
                        onOpenTask={openTask}
                    />
                ))}
            </section>
        </div>
    );
}

function KpiMini({ label, value, color }) {
    return (
        <div
            title={`${label}: indicador operativo del tablero Kanban PMO.`}
            style={{
                borderRadius: 18,
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                padding: 12,
                boxShadow: "0 12px 24px rgba(15,23,42,.05)",
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    fontWeight: 950,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                }}
            >
                {label}
            </div>
            <div
                style={{
                    marginTop: 4,
                    fontSize: 22,
                    fontWeight: 950,
                    color,
                    letterSpacing: "-.04em",
                }}
            >
                {value}
            </div>
        </div>
    );
}
